import type { RequestFn } from '../http';

/** Aufgaben & Erinnerungen (Backend: routes/todos.ts, Regeln: services/todos/todoRules.ts). */

export type TodoKind = 'PERSONAL' | 'ASSIGNED';
export type TodoListScope = 'mine' | 'delegated' | 'subscribed' | 'deputy';
export type TodoResolveWhen = 'CUSTOMER_REPLIED' | 'TICKET_CLOSED' | 'CALLBACK_DONE' | 'CUSTOMER_REACHED' | 'REQUEST_ORDERED';
export type AbsenceKind = 'VACATION' | 'SICK' | 'TRAINING' | 'OTHER';
export type TodoListView = 'today' | 'planned' | 'open' | 'done';

export interface TodoUserRef {
  id: string;
  displayName: string;
}

export interface TodoDue {
  /** ISO-Zeitpunkt */
  at: string;
  /** 'JJJJ-MM-TT' (Berlin) */
  date: string;
  /** 'HH:mm' oder null = ganzer Tag */
  time: string | null;
}

export interface TodoDueInput {
  date: string;
  time?: string | null;
}

export interface TodoLink {
  kind: 'WECLAPP_TICKET' | 'CW_TICKET' | 'CUSTOMER' | 'CALLBACK' | 'CALL' | 'MATERIAL_REQUEST';
  refId: string;
  refNumber: string | null;
  label: string | null;
  meta: { partyId?: string | null; partyName?: string | null; companyName?: string | null; phoneE164?: string | null } | null;
}

export interface TodoPermissions {
  canView: boolean;
  canEdit: boolean;
  canComplete: boolean;
  canSnooze: boolean;
  canReopen: boolean;
  canDelete: boolean;
  canComment: boolean;
  canSubscribeSelf: boolean;
  canManageSubscribers: boolean;
  canTakeOver: boolean;
}

export interface Todo {
  id: string;
  kind: TodoKind;
  source: string;
  title: string;
  notes: string | null;
  priority: number;
  assignee: TodoUserRef | null;
  createdBy: TodoUserRef | null;
  assignedAt: string;
  due: TodoDue | null;
  deadline: string | null;
  overdue: boolean;
  originalDue: TodoDue | null;
  snoozeCount: number;
  escalateAfterMinutes: number | null;
  escalatesAt: string | null;
  escalated: boolean;
  seenAt: string | null;
  completedAt: string | null;
  completedBy: TodoUserRef | null;
  completionNote: string | null;
  followUpTicket: { id: string; number: string | null } | null;
  resolveWhen: TodoResolveWhen | null;
  /** Erinnert jeden Bürotag erneut, bis erledigt (Bestellungen) */
  dailyReminder?: boolean;
  autoCompleted: boolean;
  /** Zuständiger heute abwesend */
  assigneeAbsence: { until: string; deputy: TodoUserRef | null } | null;
  links: TodoLink[];
  subscribers: TodoUserRef[];
  createdAt: string;
  updatedAt: string;
  permissions: TodoPermissions;
}

export interface TodoEvent {
  id: string;
  kind: string;
  message: string;
  user: TodoUserRef | null;
  createdAt: string;
}

export interface TodoMeta {
  isLead: boolean;
  workdayMinutes: number;
  unseenAfterMinutes: number;
  defaultEscalateAfterMinutes: number;
  escalationSteps: { minutes: number; label: string }[];
}

export interface TodoPerson {
  id: string;
  displayName: string;
  role: 'TECHNICIAN' | 'ADMIN' | 'TEAMLEAD';
  hasPush: boolean;
  absence?: { until: string; deputy: TodoUserRef | null } | null;
}

export interface TodoCounts {
  open: number;
  today: number;
  overdue: number;
  unseen: number;
  delegated: number;
  /** Offene Aufgaben der Leute, die ich gerade vertrete */
  deputy: number;
  representing: { id: string; displayName: string; until: string }[];
}

export interface TodoOverviewPerson {
  user: TodoPerson;
  absence: { until: string; kindLabel: string; deputy: TodoUserRef | null } | null;
  open: Todo[];
  stats: { open: number; overdue: number; unseen: number; escalated: number; doneLast7Days: number };
}

export interface TodoDigestItem {
  id: string;
  title: string;
  due: string | null;
  overdue: boolean;
  completedAt: string | null;
  completionNote: string | null;
  followUpTicketNumber: string | null;
  escalated: boolean;
}

export interface TodoDigest {
  day: string;
  totals: { completed: number; missed: number; escalated: number; open: number };
  people: { user: TodoUserRef | null; completed: TodoDigestItem[]; missed: TodoDigestItem[]; openCount: number }[];
}

export interface TodoInboxItem {
  id: string;
  type: string;
  title: string;
  body: string;
  url: string | null;
  relatedType: string | null;
  relatedId: string | null;
  createdAt: string;
}

export interface CreateTodoInput {
  title: string;
  notes?: string | null;
  assigneeUserId?: string;
  due?: TodoDueInput | null;
  priority?: number;
  escalateAfterMinutes?: number | null;
  subscriberIds?: string[];
  source?: 'MANUAL' | 'TICKET';
  resolveWhen?: 'CUSTOMER_REPLIED' | 'TICKET_CLOSED' | null;
  links?: {
    weclappTicket?: { id: string; number?: string | null; subject?: string | null; partyId?: string | null; partyName?: string | null };
    cwTicket?: { id: number; summary?: string | null; companyName?: string | null };
    customerId?: string;
  };
}

export interface UpdateTodoInput {
  title?: string;
  notes?: string | null;
  priority?: number;
  due?: TodoDueInput | null;
  assigneeUserId?: string;
  escalateAfterMinutes?: number | null;
  resolveWhen?: 'CUSTOMER_REPLIED' | 'TICKET_CLOSED' | null;
}

export interface Absence {
  id: string;
  user: TodoUserRef;
  startsOn: string;
  endsOn: string;
  /** Nur für den Abwesenden selbst und die Leitung, sonst null */
  kind: AbsenceKind | null;
  kindLabel: string;
  deputy: TodoUserRef | null;
  note: string | null;
  activeNow: boolean;
  canEdit: boolean;
  source: 'MANUAL' | 'OUTLOOK';
  /** Vorschlag aus Outlook, noch nicht bestätigt */
  pending: boolean;
}

export interface AbsenceInput {
  userId?: string;
  startsOn: string;
  endsOn: string;
  kind?: AbsenceKind;
  deputyUserId?: string | null;
  note?: string | null;
}

export function createTodosApi(request: RequestFn) {
  return {
    getTodoMeta() {
      return request<TodoMeta>('/todos/meta');
    },
    getTodoPeople() {
      return request<{ users: TodoPerson[] }>('/todos/people');
    },
    getTodos(params: { scope?: TodoListScope; view?: TodoListView } = {}) {
      const q = new URLSearchParams();
      if (params.scope) q.set('scope', params.scope);
      if (params.view) q.set('view', params.view);
      const qs = q.toString();
      return request<{ todos: Todo[] }>(`/todos${qs ? `?${qs}` : ''}`);
    },
    getTodoCounts() {
      return request<TodoCounts>('/todos/counts');
    },
    getTicketTodos(provider: 'weclapp' | 'cw', id: string | number) {
      return request<{ todos: Todo[] }>(`/todos/ticket?provider=${provider}&id=${encodeURIComponent(String(id))}`);
    },
    getTicketTodoIds(provider: 'weclapp' | 'cw') {
      return request<{ ids: string[] }>(`/todos/ticket-ids?provider=${provider}`);
    },
    getTodo(id: string) {
      return request<{ todo: Todo; events: TodoEvent[] }>(`/todos/${id}`);
    },
    createTodo(data: CreateTodoInput) {
      return request<{ todo: Todo }>('/todos', { method: 'POST', body: JSON.stringify(data) });
    },
    updateTodo(id: string, data: UpdateTodoInput) {
      return request<{ todo: Todo }>(`/todos/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
    },
    snoozeTodo(id: string, due: TodoDueInput) {
      return request<{ todo: Todo }>(`/todos/${id}/snooze`, { method: 'POST', body: JSON.stringify({ due }) });
    },
    extendTodoEscalation(id: string) {
      return request<{ todo: Todo }>(`/todos/${id}/escalation/extend`, { method: 'POST', body: '{}' });
    },
    completeTodo(id: string, data: { note?: string | null; followUpTicket?: { id: string; number?: string | null } } = {}) {
      return request<{ todo: Todo }>(`/todos/${id}/complete`, { method: 'POST', body: JSON.stringify(data) });
    },
    takeOverTodo(id: string) {
      return request<{ todo: Todo }>(`/todos/${id}/take-over`, { method: 'POST', body: '{}' });
    },
    getAbsences() {
      return request<{ absences: Absence[] }>('/absences');
    },
    createAbsence(data: AbsenceInput) {
      return request<{ absence: Absence }>('/absences', { method: 'POST', body: JSON.stringify(data) });
    },
    updateAbsence(id: string, data: Partial<Omit<AbsenceInput, 'userId'>>) {
      return request<{ absence: Absence }>(`/absences/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
    },
    confirmAbsence(id: string, data: { deputyUserId?: string | null; kind?: AbsenceKind }) {
      return request<{ absence: Absence }>(`/absences/${id}/confirm`, { method: 'POST', body: JSON.stringify(data) });
    },
    dismissAbsence(id: string) {
      return request<{ ok: boolean }>(`/absences/${id}/dismiss`, { method: 'POST', body: '{}' });
    },
    deleteAbsence(id: string) {
      return request<{ ok: boolean }>(`/absences/${id}`, { method: 'DELETE' });
    },
    reopenTodo(id: string) {
      return request<{ todo: Todo }>(`/todos/${id}/reopen`, { method: 'POST', body: '{}' });
    },
    commentTodo(id: string, text: string) {
      return request<{ events: TodoEvent[] }>(`/todos/${id}/comments`, { method: 'POST', body: JSON.stringify({ text }) });
    },
    subscribeTodo(id: string, userId: string) {
      return request<{ todo: Todo }>(`/todos/${id}/subscribers`, { method: 'POST', body: JSON.stringify({ userId }) });
    },
    unsubscribeTodo(id: string, userId: string) {
      return request<{ todo: Todo }>(`/todos/${id}/subscribers/${userId}`, { method: 'DELETE' });
    },
    deleteTodo(id: string) {
      return request<{ ok: boolean }>(`/todos/${id}`, { method: 'DELETE' });
    },
    getTodoOverview() {
      return request<{ people: TodoOverviewPerson[]; unassigned: Todo[] }>('/todos/overview');
    },
    getTodoDigest(day?: string) {
      return request<{ digest: TodoDigest }>(`/todos/digest${day ? `?day=${day}` : ''}`);
    },
    getTodoInbox() {
      return request<{ items: TodoInboxItem[] }>('/todos/inbox');
    },
    ackTodoInbox(ids: string[]) {
      return request<{ ok: boolean }>('/todos/inbox/ack', { method: 'POST', body: JSON.stringify({ ids }) });
    },
  };
}
